import React, { useMemo, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { BiRotateLeft } from 'react-icons/bi';

const tableHeadClasses =
  'text-left text-sm font-medium py-4 px-4 whitespace-nowrap text-center';
const tableDataClasses =
  'text-left text-sm py-5 px-4 whitespace-nowrap text-center';

// Utility function for calculating BMI
const calculateBMI = (height, weight) => {
  if (!height || !weight) return '-';
  const heightInMeters = height / 100;
  return (weight / (heightInMeters * heightInMeters)).toFixed(2);
};

function PatientTable({ data, functions }) {
  // Refactored state with descriptive names
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    column: 'created_at',
    order: 'desc',
  });
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    recordsPerPage: 5,
  });
  const [pageInput, setPageInput] = useState(paginationConfig.currentPage);



  // Memoize sorted data
  const sortedData = useMemo(() => {
    const { column, order } = sortConfig;
    return [...data].sort((a, b) => {
      if (a[column] < b[column]) return order === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Memoize filtered data
  const filteredData = useMemo(() => {
    return sortedData.filter((item) =>
      item.firstname.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [sortedData, searchQuery]);

  // Calculate pagination variables
  const totalRecords = filteredData.length;
  const totalPages = Math.ceil(totalRecords / paginationConfig.recordsPerPage);
  const { currentPage, recordsPerPage } = paginationConfig;

  // Memoize paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    return filteredData.slice(startIndex, startIndex + recordsPerPage);
  }, [filteredData, currentPage, recordsPerPage]);

  // Handlers
  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column, order: 'asc' };
    });
  };

  const getRiskTagClasses = (risk) => {
    const highTagClasses = 'bg-red-100 p-2 px-4 text-red-700 rounded-full';
    const lowTagClasses = 'bg-green-100 p-2 px-4 text-green-700 rounded-full';
    const moderateTagClasses = 'bg-blue-100 p-2 px-4 text-blue-700 rounded-full';
    const noAssessmentClasses = 'bg-gray-100 p-2 px-4 text-gray-600 rounded-full';

    if (!risk) return noAssessmentClasses;
    if (risk === 'Low Risk') return lowTagClasses;
    if (risk === 'Moderate Risk') return moderateTagClasses;
    return highTagClasses;
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setPaginationConfig((prev) => ({ ...prev, currentPage: newPage }));
      setPageInput(newPage);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setPaginationConfig((prev) => ({ ...prev, currentPage: newPage }));
      setPageInput(newPage);
    }
  };

  const handlePageJump = () => {
    const newPage = parseInt(pageInput, 10);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      setPaginationConfig((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handlePageInputKeyPress = (event) => {
    if (event.key === 'Enter') {
      handlePageJump();
    }
  };

  const handleRecordsPerPageChange = (e) => {
    const newRecords = Number(e.target.value);
    setPaginationConfig({ currentPage: 1, recordsPerPage: newRecords });
    setPageInput(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Reset pagination on search change
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
    setPageInput(1);
  };

  return (
    <div className="min-h-[600px] max-h-[calc(100vh-120px)] flex flex-col rounded-lg bg-white p-6 shadow-md">
      {/* Search Filter */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by Name..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
        </div>
        <select
          value={recordsPerPage}
          onChange={handleRecordsPerPageChange}
          className="rounded-md border border-gray-300 py-1.5 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={50}>50 per page</option>
        </select>
      </div>

      {/* Table */}
      <div className="flex-grow overflow-auto rounded-md border border-gray-200 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="min-w-[800px]">
        <table className="w-full table-auto bg-white border-collapse">
          <thead className="sticky top-0 bg-blue-900 text-white shadow-sm z-10">
            <tr className="border-b border-blue-800">
              <th className={tableHeadClasses}>Patient</th>
              <th
                className={tableHeadClasses}
                onClick={() => handleSort('createddate')}
              >
                Created At{' '}
                {sortConfig.column === 'createddate'
                  ? sortConfig.order === 'asc'
                    ? '▲'
                    : '▼'
                  : ''}
              </th>
              <th
                className={tableHeadClasses}
                onClick={() => handleSort('gender')}
              >
                Gender{' '}
                {sortConfig.column === 'gender'
                  ? sortConfig.order === 'asc'
                    ? '▲'
                    : '▼'
                  : ''}
              </th>
              <th
                className={tableHeadClasses}
                onClick={() => handleSort('height')}
              >
                BMI{' '}
                {sortConfig.column === 'height'
                  ? sortConfig.order === 'asc'
                    ? '▲'
                    : '▼'
                  : ''}
              </th>
              <th
                className={tableHeadClasses}
                onClick={() => handleSort('age')}
              >
                Age{' '}
                {sortConfig.column === 'age'
                  ? sortConfig.order === 'asc'
                    ? '▲'
                    : '▼'
                  : ''}
              </th>
              <th className={tableHeadClasses}>Assessment</th>
              <th className={tableHeadClasses}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item) => (
              <tr
                key={item.patientguid}
                className="cursor-pointer border-b border-gray-100 hover:bg-blue-50/40 transition-colors duration-150"
              >
                <td
                  className={tableDataClasses}
                  onClick={() => functions.preview(item.patientguid)}
                >
                  <h4 className="text-sm font-medium text-gray-900">{`${item.firstname} ${item.lastname}`}</h4>
                </td>
                <td
                  className={tableDataClasses}
                  onClick={() => functions.preview(item.patientguid)}
                >
                  {item.createddate}
                </td>
                <td
                  className={tableDataClasses}
                  onClick={() => functions.preview(item.patientguid)}
                >
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 ${item.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'} rounded-full text-xs font-medium`}
                  >
                    {item.gender}
                  </span>
                </td>
                <td
                  className={tableDataClasses}
                  onClick={() => functions.preview(item.patientguid)}
                >
                  {calculateBMI(item.height, item.weight)}
                </td>
                <td
                  className={tableDataClasses}
                  onClick={() => functions.preview(item.patientguid)}
                >
                  {item.age}
                </td>
                <td className={tableDataClasses}>
                  {
                    <span className={`${getRiskTagClasses(item.risk_level)}`}>
                      {item.risk_level || 'No Assessment Done'}
                    </span>
                  }
                </td>
                <td className={tableDataClasses}>
                  <Link to={`/patients/create/${item.patientguid}`}>
                    <span className="rounded-full bg-blue-100 p-2 px-4 text-blue-800">
                      <BiRotateLeft size={25} className="mx-1 inline" />
                      Retake
                    </span>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination and Records Per Page */}
      <div className="mt-6 flex items-center justify-between py-2">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            &laquo; Prev
          </button>
          <div className="flex items-center gap-2 text-sm">
            <span>Page</span>
            <input
              type="text"
              value={pageInput}
              min={1}
              max={totalPages}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={handlePageInputKeyPress}
              className="w-12 rounded-md border border-gray-300 p-1 text-center text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span>of {totalPages}</span>
          </div>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            Next &raquo;
          </button>
        </div>
      </div>
    </div>
  );
}

export default PatientTable;
